import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CalendarDays } from 'lucide-react';

export default function DailyBreakdownTable({
  data,
  orderBreakdownByDate,
  dateRange,
  filters,
}) {
  const formatCurrency = (amount) => {
    const normalized = Object.is(amount, -0) ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(normalized);
  };

  const formatDate = (dateString) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString));
  };

  const formatDateTime = (dateString, timeString) => {
    // Handle timezone conversion properly - force UTC interpretation to avoid local timezone shifts
    const dateObj = new Date(dateString + 'T00:00:00.000Z');

    const date = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(dateObj);

    if (timeString) {
      // Convert 24-hour time to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
      return `${date} ${hour12}:${minutes} ${ampm}`;
    }

    return date;
  };

  // Determine which data structure to use (prefer new structure)
  const useNewStructure = !!orderBreakdownByDate;
  const breakdownData = useNewStructure ? orderBreakdownByDate : data;

  // Helper to get totals from either structure
  const getTotals = () => {
    if (useNewStructure && orderBreakdownByDate) {
      return orderBreakdownByDate.summary;
    }
    return data?.totals;
  };

  // Helper to get daily breakdown from either structure
  const getDailyBreakdown = () => {
    if (useNewStructure && orderBreakdownByDate) {
      return orderBreakdownByDate.breakdown;
    }
    return data?.dailyBreakdown || [];
  };

  // Helper to get date range from either structure, prioritizing original request filters
  const getDateRange = () => {
    // First priority: use the original request filters if available
    if (filters) {
      return {
        startDate: filters.startDate,
        endDate: filters.endDate,
        startTime: filters.startTime,
        endTime: filters.endTime,
      };
    }
    // Fallback to existing logic
    if (dateRange)
      return { ...dateRange, startTime: undefined, endTime: undefined };
    if (useNewStructure && orderBreakdownByDate) {
      return {
        startDate: orderBreakdownByDate.dateRange.startDate,
        endDate: orderBreakdownByDate.dateRange.endDate,
        startTime: undefined,
        endTime: undefined,
      };
    }
    if (data?.dateRange) {
      return {
        startDate: data.dateRange.start,
        endDate: data.dateRange.end,
        startTime: undefined,
        endTime: undefined,
      };
    }
    return null;
  };

  const totals = getTotals();
  const dailyBreakdown = getDailyBreakdown();
  const effectiveDateRange = getDateRange();

  // Return early if no data
  if (!totals || !dailyBreakdown.length) {
    return null;
  }

  const rows = [
    {
      label: 'Gross sales',
      field: 'grossSales',
      className:
        'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    },
    {
      label: 'Discounts',
      field: 'discounts',
      className: 'text-red-600 dark:text-red-400',
    },
    {
      label: 'Refunds',
      field: 'refunds',
      className: 'text-red-600 dark:text-red-400',
    },
    {
      label: 'Net sales',
      field: 'netSales',
      className:
        'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold',
    },
    {
      label: 'Non-revenue items (delivery fees, service fees, etc)',
      field: 'nonRevenueItems',
      className: 'text-gray-600 dark:text-gray-400',
    },
    {
      label: 'Taxes',
      field: 'taxesExpected',
      className: 'text-gray-700 dark:text-gray-300',
    },
    // {
    //   label: "Taxes collected",
    //   field: "taxesCollected" as const,
    //   className:
    //     "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 font-medium",
    // },
    {
      label: 'Tips',
      field: 'tips',
      className: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Amount collected',
      field: 'amountCollected',
      className:
        'bg-gray-50 dark:bg-gray-800 font-semibold border-t-2 border-gray-300 dark:border-gray-600',
    },
    {
      label: 'Unpaid balance',
      field: 'unpaidBalance',
      className: 'text-orange-600 dark:text-orange-400',
    },
  ];

  return (
    <Card className="print-card w-full">
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <CalendarDays className="mr-2 h-6 w-6 text-blue-600" />
          Daily Breakdown
        </CardTitle>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Financial breakdown from{' '}
          {effectiveDateRange
            ? formatDateTime(
                effectiveDateRange.startDate,
                effectiveDateRange.startTime
              )
            : 'N/A'}{' '}
          to{' '}
          {effectiveDateRange
            ? formatDateTime(
                effectiveDateRange.endDate,
                effectiveDateRange.endTime
              )
            : 'N/A'}
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700">
                  {/* Empty header for row labels */}
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 min-w-[100px]">
                  Total
                </th>
                {dailyBreakdown.map((day) => (
                  <th
                    key={day._id}
                    className="text-right py-3 px-4 font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 min-w-[100px]"
                  >
                    {day._id}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.field}
                  className={`border-b border-gray-100 dark:border-gray-800 ${
                    row.className || ''
                  }`}
                >
                  <td className="py-2 px-4 font-medium text-left">
                    {row.label}
                  </td>
                  <td className="py-2 px-4 text-right font-medium">
                    {formatCurrency(
                      row.field === 'refunds' || row.field === 'discounts'
                        ? -Math.abs(totals[row.field] || 0)
                        : totals[row.field] || 0
                    )}
                  </td>
                  {dailyBreakdown.map((day) => (
                    <td
                      key={`${day._id}-${row.field}`}
                      className="py-2 px-4 text-right"
                    >
                      {formatCurrency(
                        row.field === 'refunds' || row.field === 'discounts'
                          ? -Math.abs(day[row.field] || 0)
                          : day[row.field] || 0
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
